import React, { useState } from 'react';
import { TbChevronRight, TbCircleCheckFilled, TbTrash } from 'react-icons/tb';
import { ADDRESS_TYPE_LOCALE, ADDRESS_TYPE_NOT_LISTED, ADDRESS_TYPE_STORE_LIST, getIconAddress } from '../../Utils/Constants/Global';
import { formatZipcode } from '../../Utils/Formatters';
import { useStoreContext } from '../../Contexts/StoreContext';
import ButtonDelete from '../Form/ButtonDelete';
import { useLoginContext } from '../../Contexts/LoginContext';
import { useOrderTypeContext } from '../../Contexts/OrderTypeContext';
import CustomerService from '../../Services/CustomerService';
import { ImSpinner8 } from 'react-icons/im';
import Modal from './Modal';
import Confirm from './Confirm';
import { useTranslation } from 'react-i18next';

interface SlotAddressChoiceProps {
    address: any;
    onHandleClick?: CallableFunction;
    isAddressSaved?: boolean;
    hasDelete?: boolean;
    className?: string;
    outline?: boolean;
    onDelete?: CallableFunction;
    isCurrent?: boolean;
}

const SlotAddressChoice = ({
    onHandleClick = () => {},
    address,
    isAddressSaved = false,
    hasDelete = false,
    className,
    outline=false,
    onDelete,
    isCurrent=false
    }: SlotAddressChoiceProps) => {

        const { t } = useTranslation();
        const { currentAddress } = useStoreContext();

        const { isAuthenticated } = useLoginContext();
        const { orderType } = useOrderTypeContext();
        const [ showConfirmDelete, setShowConfirmDelete ] = useState(false);
        const [ loadingDeleteAddresses, setLoadingDeleteAddresses ] = useState(false);

        const handleClick = (address: any) => {
            onHandleClick(address)
        }

        const handleDelete = async (address_id: number) => {
            setShowConfirmDelete(false);
            if (!isAuthenticated || orderType === null) return;
            try {
                setLoadingDeleteAddresses(true);
                const customerService = new CustomerService(orderType);
                await customerService.deleteAddress(address_id);
                onDelete?.();
            }
            catch (error: any) {
                setLoadingDeleteAddresses(false);
            }
        }

        const addressIsCurrent = isCurrent || (currentAddress?.id != null && currentAddress.id === address.id);
                            
        return (
            <>
                <button className={`
                    relative flex items-center w-full gap-3 p-3 bg-white rounded-lg transition-all ease-in-out 
                    ${addressIsCurrent ? 'ring-2 ring-custom-primary' : outline ? 'ring-1 ring-gray-200' : 'ring-transparent hover:shadow-md hover:shadow-gray-100'}
                    ${loadingDeleteAddresses ? 'opacity-75' : ''}
                    ${hasDelete ? 'cursor-default' : 'hover:shadow-md hover:shadow-gray-100'}
                    ${className}
                    `} 
                    onClick={() => handleClick(address)}>
                    
                    {addressIsCurrent &&
                        <TbCircleCheckFilled size={28} className='absolute -right-2 -top-2 bg-white rounded-full text-custom-primary zoomInIconNoDelay' />
                    }

                    <div className={`
                        ${ 
                            address.title === ADDRESS_TYPE_LOCALE || address.title === ADDRESS_TYPE_NOT_LISTED || address.title === ADDRESS_TYPE_STORE_LIST
                            ? 'text-custom-primary transition-all ease-in-out' : ''
                        }
                        `}>{ getIconAddress(address.title) }</div>
                    <div className={`grid gap-1 text-left w-full leading-none`}>
                        {(isAddressSaved || hasDelete) && <span className='text-xs leading-none'>{ address.title }</span>}
                        <span className='font-medium'>{ address.main_text }</span>
                        {address.secondary_text && <span className='text-gray-400 leading-none text-sm'>
                            { address.secondary_text }
                            { address.zipcode && <> - CEP:&nbsp;{ formatZipcode(address.zipcode) }</> }
                        </span>}
                    </div>
                    {hasDelete 
                        ? <ButtonDelete rounded onClick={() => setShowConfirmDelete(true)} className={`${loadingDeleteAddresses && 'pointer-events-none'}`}>
                                <div>{loadingDeleteAddresses ? <ImSpinner8 className="animate-spin" size={20} /> : <TbTrash size={20} /> }</div>
                            </ButtonDelete>
                        : <div><TbChevronRight size={20} className="text-gray-200" /></div>
                    }
                </button>

                <Modal open={showConfirmDelete} className="max-w-sm m-4" onClose={() => setShowConfirmDelete(false)}>
                    <Confirm
                        title={t('profile.addresses.confirm_remove.title')}
                        message={t('profile.addresses.confirm_remove.message', {address: address.main_text})}
                        labelConfirm={t('profile.addresses.confirm_remove.label_confirm')}
                        onConfirm={() => handleDelete(address.id)}
                        onCancel={() => setShowConfirmDelete(false)}
                    />
                </Modal>
            </>
    )
}

export default SlotAddressChoice;